import { isDefined, isEmpty } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { isNotEmpty } from '@/components/sg-design-system/utils/typescript-helpers.ts';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorFixingReference2Data,
  selectTargetAccumulatorisCrossedData,
} from '@/store/targetAccumulator/selectors.ts';

const FixingReference2 = () => {
  const tileId = useTargetAccumulatorTileId();
  const { fixingReference2, fixingReference2List, fixingReference2Default } = useAppSelector((state) =>
    selectTargetAccumulatorFixingReference2Data(state, tileId),
  );
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { isCrossed, crossCurrency } = useAppSelector((state) => selectTargetAccumulatorisCrossedData(state, tileId));
  const currencyPair = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.currencyPair);
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : [undefined, undefined];

  const shouldDisplaySecondFixingReference =
    crossCurrency !== currency2 && crossCurrency !== currency1 && isNotEmpty(crossCurrency);

  const currencyPairDisplayed = shouldDisplaySecondFixingReference ? `${crossCurrency}/${currency2}` : currencyPair;

  const isSameReferenceAsDefault = fixingReference2 === fixingReference2Default;
  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleFixingReference2 = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Fixing reference 2 changed',
      isDefined(event.target.value) ? event.target.value : 'cleared',
    );

    return checkTargetAccumulatorWithTradeCapture({
      fixingReference2: event.target.value,
    });
  };

  const shouldDisplayFixingReference2 =
    isCrossed && isDefined(crossCurrency) && !isEmpty(fixingReference2List) && shouldDisplaySecondFixingReference;
  if (!shouldDisplayFixingReference2) {
    return null;
  }

  return (
    <TargetAccumulatorFormField
      formattedMessageId="targetAccumulator.form.fixingReference"
      formattedMessageValues={{ currencyPair: currencyPairDisplayed ?? '' }}
      extra={
        !isSameReferenceAsDefault ? (
          <Tooltip
            position="right"
            contents={
              <FormattedMessage
                id="targetAccumulator.form.fixingReference.info"
                values={{ defaultFixingReference: fixingReference2Default, br: <br /> }}
              />
            }
          >
            <div className="badge badge-dismissible">
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
            </div>
          </Tooltip>
        ) : undefined
      }
    >
      <Form.Select
        value={fixingReference2 ?? fixingReference2Default}
        onChange={handleFixingReference2}
        size="sm"
        className={`${!isSameReferenceAsDefault ? 'text-warning fw-bold' : ''}`}
        data-e2e="fixing-reference2-selector"
      >
        {fixingReference2List?.map((ref) => (
          <option className="text-primary" key={ref} value={ref}>
            {ref}
          </option>
        ))}
      </Form.Select>
    </TargetAccumulatorFormField>
  );
};

export default FixingReference2;
